import React, { useContext, useState } from 'react';
import * as Icon from 'react-bootstrap-icons';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Link
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home,
  Search,
  Tag,
  EmojiObjects as Lightbulb,
  Person,
  Group,
  EmojiEvents as Crown,
  Assignment,
  LibraryBooks,
  Description,
  Settings,
  Language,
  Close,
  Info,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router';
import { LoginInfo } from '../../App';
import { LinkContainer } from 'react-router-bootstrap';
import { useTranslation } from 'react-i18next';
import SettingPopup from '../SettingPopup';
import SettingsIcon from '@mui/icons-material/Settings';

const SideMenu = () => {
  const location = useLocation()
  // ログイン情報
  const { loginInfo, setLoginInfo } = useContext(LoginInfo)
  const { t, i18n } = useTranslation()
  const [showSetting, setShowSetting] = useState(false)
  const navigate = useNavigate()
  function changeLanguage(lang) {
    let s = localStorage
    s.removeItem("language")
    s.setItem("language", lang)
    i18n.changeLanguage(lang)
  }

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isHome = location.pathname === '/';
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [isOpen, setIsOpen] = useState(false);
  const [languageMenu, setLanguageMenu] = useState(null);

  const isPermanent = isHome && isLargeScreen;

  const handleDrawerToggle = () => {
    if (!isPermanent) {
      setIsOpen(!isOpen);
    }
  };

  const handleLanguageClick = (event) => {
    setLanguageMenu(event.currentTarget);
  };

  const handleLanguageClose = (lang) => {
    if (lang) {
      changeLanguage(lang);
    }
    setLanguageMenu(null);
  };

  const menuItems = [
    {
      title: t('メイン'),
      items: [
        { text: t('category.home'), icon: <Home />, path: '/' },
        { text: t('common.AIGeneration'), icon: <Description />, path: '/upload/image/generate' }
      ]
    },
    {
      title: t('検索'),
      items: [
        { text: t('category.search'), icon: <Search />, path: '/search/*/1' },
        { text: t('category.tag'), icon: <Tag />, path: '/tag/*/1' }
      ]
    },
    {
      title: t('コンテンツ'),
      items: [
        { text: t('category.discover'), icon: <Lightbulb />, path: '/random' },
        { text: t('キャラクター'), icon: <Person />, path: '/search_characters' },
        { text: t('クリエイター'), icon: <Group />, path: '/search_users' }
      ]
    },
    {
      title: t('ランキング・トピック'),
      items: [
        { text: t('category.ranking'), icon: <Crown />, path: '/ranking' },
        { text: t('category.odai'), icon: <Assignment />, path: '/odai' },
        { text: t('category.new'), icon: <LibraryBooks />, path: '/new' }
      ]
    },
    {
      title: t('規約・設定'),
      items: [
        { text: t("category.terms"), icon: <Description />, path: '/terms' },
        { text: t("common.guideline"), icon: <Description />, path: '/guideline' },
        { text: t("category.setting"), icon: <Settings />, path: '/settings' },
        { text: t("category.information"), icon: <Info />, path: '/about' },
      ]
    }
  ];

  const drawer = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* ヘッダー部分 */}
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '64px'
      }}>
        <LinkContainer to="/" style={{ textDecoration: "none", color: "#000000" }}>
          <Typography variant="h6" component="div">
            <span className="App-logo d-none d-sm-inline-block" id="logo" style={{ fontSize: "18px", cursor: "pointer" }}><img src='/img/logo_new.png' height={18} /></span> <span className='d-none d-sm-inline-block' style={{ fontSize: "8px" }}>JP</span>
          </Typography>
        </LinkContainer>
        {!isPermanent &&
        <IconButton onClick={handleDrawerToggle}>
          <Close />
        </IconButton>}
      </Box>

      <Divider />

      {/* 言語切り替えとプレミアム */}
      <Box>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLanguageClick}>
            <ListItemIcon>
              <Language />
            </ListItemIcon>
            <ListItemText
              //primary={t('言語')}
              secondary={
                i18n.language === 'ja' ? '日本語' :
                  i18n.language === 'en' ? 'English' :
                    i18n.language === 'zhtw' ? '中文(繁體)' :
                      i18n.language === 'zhCN' ? '中文(简体)' :
                        i18n.language === 'ko' ? '한국어' :
                          i18n.language
              }
            />
          </ListItemButton>
        </ListItem>
        <Menu
          anchorEl={languageMenu}
          open={Boolean(languageMenu)}
          onClose={handleLanguageClose}
        >
          <MenuItem onClick={(e) => { handleLanguageClose("ja") }}>日本語</MenuItem>
          <MenuItem onClick={(e) => { handleLanguageClose("en") }}>English</MenuItem>
          <MenuItem onClick={(e) => { handleLanguageClose("zhtw") }}>中文(繁體)</MenuItem>
          <MenuItem onClick={(e) => { handleLanguageClose("zhCN") }}>中文(简体)</MenuItem>
          <MenuItem onClick={(e) => { handleLanguageClose("ko") }}>한국어</MenuItem>
        </Menu>
        <ListItem>
          <ListItemButton
            onClick={() => navigate("/legal")}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              color: 'white',
              boxShadow: '0 2px 10px rgba(102, 126, 234, 0.3)',
              transition: 'all 0.4s ease',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(120deg, transparent, rgba(255,255,255,0.2), transparent)',
                transition: '0.5s',
              },
              '&:hover::before': {
                left: '100%',
              },
              '&:hover': {
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.5)',
              }
            }}
          >
            <ListItemIcon sx={{ color: 'white' }}>
              <Typography>✨</Typography>
            </ListItemIcon>
            <ListItemText
              primary={t("common.premium")}
              primaryTypographyProps={{
                sx: {
                  fontWeight: 'bold',
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }
              }}
            />
          </ListItemButton>
        </ListItem>
      </Box>

      <Divider />

      {/* メインメニュー - スクロール可能 */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'background.paper',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'grey.500',
          borderRadius: '4px',
        }
      }}>
        {menuItems.map((section, index) => (
          <Box key={section.title}>
            <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
              {section.title}
            </Typography>
            <List>
              {section.items.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (section.title === "規約・設定" && item.path === "/settings") {
                        setShowSetting(true)
                        handleDrawerToggle()
                        return
                      }
                      navigate(item.path);
                      if (isMobile) {
                        handleDrawerToggle();
                      }
                    }}
                    selected={location.pathname === item.path}
                    sx={{
                      '&.Mui-selected': {
                        bgcolor: 'action.selected',
                        '&:hover': {
                          bgcolor: 'action.selected',
                        }
                      }
                    }}
                  >
                    <ListItemIcon>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            {index < menuItems.length - 1 && <Divider />}
          </Box>
        ))}
        {loginInfo.role && loginInfo.role.find(d => d.role_div === "admin" || d.role_div === "moderator") ? (
        <ListItemButton
          onClick={() => navigate("/siteManage")}
          sx={{
            background: 'linear-gradient(135deg, #0093E9 0%, #80D0C7 100%)',
            borderRadius: 2,
            color: 'white',
            boxShadow: '0 2px 10px rgba(0, 147, 233, 0.3)',
            transition: 'all 0.4s ease',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(120deg, transparent, rgba(255,255,255,0.2), transparent)',
              transition: '0.5s',
            },
            '&:hover::before': {
              left: '100%',
            },
            '&:hover': {
              boxShadow: '0 4px 15px rgba(0, 147, 233, 0.5)',
              transform: 'translateY(-1px)',
            }
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText
            primary="サイト管理"
            primaryTypographyProps={{
              sx: {
                fontWeight: 'bold',
                textShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }
            }}
          />
        </ListItemButton>
        ) : ""}
      </Box>

      {/* フッター部分 */}
      <Box>
        <Divider />
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-around' }}>
          <Link href={"https://lin.ee/9JVODFH"} target='_blank' style={{ fontSize: "28px", textDecoration: "none" }}><Icon.Line /></Link>
          &nbsp;&nbsp;
          <Link href={"//x.com/ourt_ai"} target='_blank' style={{ fontSize: "28px", textDecoration: "none" }}>𝕏</Link>
          &nbsp;&nbsp;
          <Link href={"//discord.gg/DTsR7gnf6x"} target='_blank' style={{ fontSize: "28px" }}><Icon.Discord /></Link>
          &nbsp;&nbsp;
          <Link href={"//sns.ourt-ai.work"} target='_blank' style={{ fontSize: "30px" }}><img src="/img/misskey.png" width="36px" className='pb-1' /></Link>
        </Box>
      </Box>
    </Box>
  );

  // メニュートグルボタン
  const menuButton = (
    <IconButton
      color="inherit"
      aria-label="toggle drawer"
      onClick={handleDrawerToggle}
      sx={{
        position: 'fixed',
        left: 16,
        top: 20,
        zIndex: 4,
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <MenuIcon />
    </IconButton>
  );

  return (
    <>
      {showSetting ? (<SettingPopup switch={setShowSetting} />) : null}
      
      {!isPermanent && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            left: 16,
            top: 0,
            zIndex: theme.zIndex.appBar + 1,
            bgcolor: 'background.paper',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}
      
      <Box component="nav" sx={{ width: isPermanent ? 280 : 'auto' }}>
        <Drawer
          variant={isPermanent ? 'permanent' : 'temporary'}
          open={isPermanent || isOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
              zIndex: theme.zIndex.appBar + 1,
              ...(isPermanent && {
                position: 'fixed',
                height: '100vh',
              })
            }
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </>
  );
};

export default SideMenu;